import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@utils'
import { useSessionStore } from '@stores'
import { InputType } from '@constants/enums'
import OrderItemStatus from './OrderItemStatus'
import SelectItems from './SelectItems'
import StepTitle from './StepTitle'

interface OrderItemsProps {
  input?: {
    type: number
    label: string
    formatted_label: string
    [key: string]: any
  }
  items?: OrderItem[]
  children?: React.ReactNode
  noback?: boolean
  onSubmit?: (input: any) => void
  onBack?: () => void
  onEdit?: (input: any, item: OrderItem) => void
  // Popup flows (OMP action drawer) pass the live popup session; falls back to the store.
  session?: any
}

interface OrderItem {
  id: number
  name: string
  quantity: number
  price: number
  [key: string]: any
}

const OrderItems = ({
  input,
  items = [],
  children,
  noback = false,
  onSubmit,
  onBack,
  onEdit,
  session: sessionProp,
}: OrderItemsProps) => {
  const [slide, setSlide] = useState(0)
  const [showItemDetails, setShowItemDetails] = useState(false)
  const [canSelectItems, setCanSelectItems] = useState(false)
  const [statusItems, setStatusItems] = useState<OrderItem[]>([])
  const touchStartXRef = useRef<number | null>(null)
  const touchDeltaXRef = useRef<number>(0)

  const { session: storeSession, selectItem, setStatusItems: storeSetStatusItems } = useSessionStore()
  const session = sessionProp ?? storeSession

  const showTitle = useMemo(
    () =>
      !input ||
      input.type !== InputType.InputActionStatus ||
      !session ||
      !session.is_popup ||
      (statusItems && statusItems.length > 1),
    [input, session, statusItems]
  )

  const canGoBack = useMemo(
    () =>
      !noback &&
      ((input && input.type === InputType.InputActionStatus) ||
        (session && session.can_undo) ||
        (session as any)?.is_step_preview ||
        (showItemDetails && canSelectItems)),
    [input, session, showItemDetails, canSelectItems, noback]
  )

  const handleDone = useCallback(() => {
    if (onSubmit && input) {
      onSubmit(input)
    }
  }, [onSubmit, input])

  const handleGoBack = useCallback(() => {
    if (showItemDetails && canSelectItems) {
      setShowItemDetails(false)
    } else if (onBack) {
      onBack()
    }
  }, [showItemDetails, canSelectItems, onBack])

  const handleItemsSelected = useCallback(
    (selectedItems: OrderItem[]) => {
      if (selectedItems.length) {
        setStatusItems(selectedItems)
        storeSetStatusItems(selectedItems)
        setShowItemDetails(true)
      } else {
        handleDone()
      }
    },
    [storeSetStatusItems, handleDone]
  )

  const handleEditItem = useCallback(
    (editInput: any, item: OrderItem) => {
      selectItem(item)
      if (onEdit) {
        onEdit(editInput, item)
      }
    },
    [selectItem, onEdit]
  )

  useEffect(() => {
    setStatusItems(items)
    storeSetStatusItems(items)

    if (items.length) {
      setCanSelectItems(false)
      setShowItemDetails(true)

      if (session?.selected_item) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].id === session.selected_item.id) {
            setSlide(i)
            break
          }
        }
      }
    } else {
      setCanSelectItems(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="w-full">
      {showItemDetails ? (
        <>
          <div className={cn('text-center mx-auto', !showTitle && 'mt-5')}>
            {showTitle && (
              <StepTitle hr>
                Item Details
                {statusItems.length > 1 && (
                  <div className="selected-item">
                    {slide + 1}/{statusItems.length}
                  </div>
                )}
                {statusItems.length > 1 && (
                  <div className="step-subtitle">
                    <span className="hidden md:inline-block">Use the side arrows</span>
                    <span className="md:hidden">Swipe sideways</span>
                    <span> to review more items</span>
                  </div>
                )}
              </StepTitle>
            )}
          </div>

          <div
            className="item-status-carousel"
            onTouchStart={e => {
              touchStartXRef.current = e.touches[0].clientX
              touchDeltaXRef.current = 0
            }}
            onTouchMove={e => {
              if (touchStartXRef.current === null) {
                return
              }
              touchDeltaXRef.current = e.touches[0].clientX - touchStartXRef.current
            }}
            onTouchEnd={() => {
              const threshold = 50
              if (touchDeltaXRef.current > threshold && slide > 0) {
                setSlide(slide - 1)
              } else if (touchDeltaXRef.current < -threshold && slide < statusItems.length - 1) {
                setSlide(slide + 1)
              }
              touchStartXRef.current = null
              touchDeltaXRef.current = 0
            }}>
            {statusItems.length > 1 && (
              <div className="carousel-controls">
                <button
                  type="button"
                  className="carousel-control"
                  onClick={() => setSlide(Math.max(0, slide - 1))}
                  disabled={slide === 0}
                  aria-label="Previous item">
                  <ChevronLeft size={24} />
                </button>
                <button
                  type="button"
                  className="carousel-control"
                  onClick={() => setSlide(Math.min(statusItems.length - 1, slide + 1))}
                  disabled={slide === statusItems.length - 1}
                  aria-label="Next item">
                  <ChevronRight size={24} />
                </button>
              </div>
            )}

            <div className="carousel-inner">
              <div className="carousel-items" style={{ transform: `translateX(-${slide * 100}%)` }}>
                {statusItems.map(item => (
                  <div key={`item-${item.id}`} className="carousel-item">
                    <OrderItemStatus item={item} onEdit={handleEditItem} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : canSelectItems ? (
        <div className="product-list">
          {showTitle && children}
          <SelectItems input={input} noback onSelect={handleItemsSelected} />
        </div>
      ) : null}

      <div className="flex justify-center gap-3 mb-3 w-full max-w-lg mx-auto">
        {canGoBack && (
          <button type="button" onClick={handleGoBack} className="btn-base back-btn flex-1">
            Back
          </button>
        )}
        <button
          type="button"
          onClick={handleDone}
          className="btn-base flex-1"
          style={{
            backgroundColor: 'var(--theme-color)',
            color: 'var(--button-text-color, #fff)',
            borderColor: 'var(--theme-color)',
          }}>
          {input?.formatted_label ? <span dangerouslySetInnerHTML={{ __html: input.formatted_label }} /> : 'Continue'}
        </button>
      </div>
    </div>
  )
}

export default OrderItems
